import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import saveAs from 'file-saver';
import { AutoFocusDirective, ButtonComponent, InputComponent, TextEllipsisComponent, TileComponent } from 'mbs-ui-kit';
import { Guide } from 'mbs-ui-shared@components/sidepanel-downloads/models/guide';
import { debounceTime, startWith } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-guides-tab',
  templateUrl: './guides-tab.component.html',
  imports: [
    AutoFocusDirective,
    CommonModule,
    ReactiveFormsModule,
    TileComponent,
    ButtonComponent,
    TextEllipsisComponent,
    I18NextPipe,
    InputComponent
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GuidesTabComponent implements OnInit {
  public destroyRef = inject(DestroyRef);
  public search = new FormControl('');

  public loadingFileLink = signal('');

  public filteredGuides: WritableSignal<Array<Guide>> = signal([]);
  public guides: WritableSignal<Array<Guide>> = signal([
    /* {
      title: 'Legacy backup and restore best practices',
      extension: 'PDF',
      link: 'https://get.msp360.com/hubfs/Branding%20-%202025%20Legacy%20Backup%20and%20restore%20best%20practices.pdf'
    },*/
    {
      title: 'NBF backup and restore best practices',
      extension: 'PDF',
      link: 'https://download.msp360.com/mbs/pdf/2025-NBF-Backup-and-Restore-Best-Practices.pdf'
    },
    {
      title: 'Backup schedule and retention',
      extension: 'PDF',
      link: 'https://download.msp360.com/mbs/pdf/Best-Practices-for-MSP360-Backup-Schedule-and-Retention.pdf'
    },
    {
      title: 'Onboarding new customers checklist',
      extension: 'PDF',
      link: 'https://www.msp360.com/download/onboarding-new-customer-checklist.pdf'
    },
    {
      title: 'Security best practices',
      extension: 'PDF',
      link: 'https://www.msp360.com/download/msp360-managed-backup-security-best-practices.pdf'
    },
    {
      title: 'Basic backup strategies',
      extension: 'PDF',
      link: 'https://www.msp360.com/download/basic-backup-strategies.pdf'
    },
    {
      title: 'Daily, monthly, weekly routines',
      extension: 'PDF',
      link: 'https://www.msp360.com/download/whitepapers/msp-workflow-core-tasks-streamlined-by-backup-and-rmm-solutions.pdf'
    }
  ]);

  ngOnInit() {
    this.search.valueChanges
      .pipe(startWith(this.search.value), debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.setFilteredGuides(value));
  }

  public setFilteredGuides(value: string) {
    this.filteredGuides.set(this.guides().filter((guide) => guide.title.toLowerCase().includes(value.toLowerCase())));
  }

  public download(link: string, name: string) {
    this.loadingFileLink.set(link);

    fetch(link)
      .then(async (response) => {
        const blob = await response.blob();

        saveAs(blob, `${name}.pdf`);
        this.loadingFileLink.set('');
      })
      .catch((e) => {
        window.open(link, '_blank');
        this.loadingFileLink.set('');
      });
  }
}
